import { createContext, useContext, useId, type ReactNode } from 'react'

import { cn } from '../cn'

export interface FieldContextValue {
  readonly id: string
  readonly describedBy: string | undefined
  readonly invalid: boolean
  readonly required: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

/** Inputs inside a FormField pick up id / aria-describedby / aria-invalid automatically. */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext)
}

export interface FormFieldProps {
  readonly label: ReactNode
  readonly hint?: ReactNode
  readonly error?: string | undefined
  readonly required?: boolean
  readonly className?: string
  readonly children: ReactNode
  /** For controls that are not a single input (radio groups): render a fieldset/legend. */
  readonly as?: 'div' | 'fieldset'
}

export function FormField({
  label,
  hint,
  error,
  required = false,
  className,
  children,
  as = 'div',
}: FormFieldProps) {
  const base = useId()
  const id = `${base}-control`
  const hintId = hint === undefined ? undefined : `${base}-hint`
  const errorId = error === undefined ? undefined : `${base}-error`
  const describedBy =
    [errorId, hintId].filter((v): v is string => v !== undefined).join(' ') || undefined
  const Tag = as
  const LabelTag = as === 'fieldset' ? 'legend' : 'label'

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: error !== undefined, required }}>
      <Tag className={cn('mb-4 min-w-0', className)}>
        <LabelTag
          htmlFor={as === 'fieldset' ? undefined : id}
          className="mb-1.5 block text-[12.5px] font-semibold text-ink-2"
        >
          {label}
          {required && (
            <span aria-hidden className="ml-0.5 text-lost">
              *
            </span>
          )}
        </LabelTag>
        {children}
        {hint !== undefined && (
          <p id={hintId} className="mt-1.5 text-[12px] text-muted">
            {hint}
          </p>
        )}
        {error !== undefined && (
          <p id={errorId} role="alert" className="mt-1.5 text-[12px] font-medium text-lost">
            {error}
          </p>
        )}
      </Tag>
    </FieldContext.Provider>
  )
}
